<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import NxButton from '../Button/NxButton.vue';
import NxSearchInput from '../Inputs/NxSearchInput.vue';

export interface NxSettingsShellTab {
  id: string;
  label: string;
  description?: string;
  group?: string;
  /**
   * Extra search terms (names of the controls inside the tab). Only matched by
   * the `searchable` filter, never rendered.
   */
  keywords?: string[];
}

export interface NxSettingsShellGroup {
  id: string;
  label: string;
}

const props = withDefaults(
  defineProps<{
    /** Tabs rendered in the sidebar navigation, in order. */
    tabs: NxSettingsShellTab[];
    /** Optional group definitions; tabs reference them via `tab.group`. */
    groups?: NxSettingsShellGroup[];
    /** Small uppercase line above the title (e.g. product name). */
    eyebrow?: string;
    title: string;
    /** Label of the close button; the button is omitted when empty. */
    closeLabel?: string;
    /** Prefix for the generated tab/panel element ids. */
    idPrefix?: string;
    /**
     * Opt-in search field above the tab list: it filters the tabs by label,
     * description and hidden `keywords` (groups without a hit disappear). `v-model:search` is optional.
     */
    searchable?: boolean;
    /** Placeholder of the search field. */
    searchPlaceholder?: string;
    /** Accessible name of the search field. */
    searchLabel?: string;
    /** Short note shown in the tab list when the search finds nothing. */
    searchEmptyText?: string;
  }>(),
  {
    groups: () => [],
    eyebrow: '',
    closeLabel: '',
    idPrefix: 'nx-settings',
    searchable: false,
    searchPlaceholder: 'Suche',
    searchLabel: 'Einstellungen durchsuchen',
    searchEmptyText: 'Keine passende Einstellung gefunden.',
  }
);

const emit = defineEmits<{ close: [] }>();

const activeTab = defineModel<string>({ required: true });

/** The search text (opt-in `v-model:search`; local when not bound). */
const search = defineModel<string>('search', { default: '' });

/** Case- and accent-insensitive form for matching („Übersicht“ ↔ „ubersicht“). */
function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();
}

/** Every word of the query must occur in the tab's label or description. */
const visibleTabs = computed(() => {
  const words = props.searchable ? normalize(search.value).split(/\s+/).filter(Boolean) : [];
  if (words.length === 0) return props.tabs;
  return props.tabs.filter((tab) => {
    const haystack = normalize(
      [tab.label, tab.description ?? '', ...(tab.keywords ?? [])].join(' ')
    );
    return words.every((word) => haystack.includes(word));
  });
});

const groupedTabs = computed(() => {
  if (props.groups.length === 0) {
    return [{ id: '', label: '', tabs: visibleTabs.value }];
  }
  const fallbackGroup = props.groups[0]?.id;
  return props.groups
    .map((group) => ({
      ...group,
      tabs: visibleTabs.value.filter((tab) => (tab.group ?? fallbackGroup) === group.id),
    }))
    .filter((group) => group.tabs.length > 0);
});

const activeTabDefinition = computed(
  () => props.tabs.find((tab) => tab.id === activeTab.value) ?? props.tabs[0]
);

/**
 * The tab that takes the tab stop (roving tabindex): the active one, or — when
 * the search filtered it out — the first hit, so the list stays reachable.
 */
const focusTabId = computed(() => {
  const rendered = groupedTabs.value.flatMap((group) => group.tabs);
  if (rendered.some((tab) => tab.id === activeTabDefinition.value?.id)) {
    return activeTabDefinition.value.id;
  }
  return rendered[0]?.id;
});

const tabListRef = useTemplateRef<HTMLElement>('tab-list');

function onTabKeydown(event: KeyboardEvent, tabId: string): void {
  const renderedTabs = groupedTabs.value.flatMap((group) => group.tabs);
  const currentIndex = renderedTabs.findIndex((tab) => tab.id === tabId);
  if (currentIndex < 0 || renderedTabs.length === 0) return;

  let targetIndex: number | null = null;
  switch (event.key) {
    case 'ArrowRight':
      targetIndex = (currentIndex + 1) % renderedTabs.length;
      break;
    case 'ArrowLeft':
      targetIndex = (currentIndex - 1 + renderedTabs.length) % renderedTabs.length;
      break;
    case 'Home':
      targetIndex = 0;
      break;
    case 'End':
      targetIndex = renderedTabs.length - 1;
      break;
    default:
      return;
  }

  event.preventDefault();
  const targetTab = renderedTabs[targetIndex];
  activeTab.value = targetTab.id;
  tabListRef.value?.querySelectorAll<HTMLButtonElement>('[role="tab"]').item(targetIndex)?.focus();
}
</script>

<template>
  <section class="nx-settings-shell" :class="{ 'is-searchable': searchable }" :aria-label="title">
    <header class="nx-settings-shell-header">
      <div class="nx-settings-shell-titles">
        <p v-if="eyebrow || $slots['eyebrow-icon']" class="nx-settings-shell-eyebrow">
          <slot name="eyebrow-icon" />
          {{ eyebrow }}
        </p>
        <h2 :id="`${idPrefix}-title`">{{ title }}</h2>
      </div>
      <div class="nx-settings-shell-header-actions">
        <slot name="header-actions" />
        <NxButton
          v-if="closeLabel"
          height="xs"
          type="button"
          variant="outline"
          width="narrow"
          @click="emit('close')"
        >
          {{ closeLabel }}
        </NxButton>
      </div>
    </header>

    <div v-if="searchable" class="nx-settings-shell-search" data-nx-scope="sidebar" role="search">
      <NxSearchInput
        v-model="search"
        height="s"
        :placeholder="searchPlaceholder"
        :aria-label="searchLabel"
        data-testid="nx-settings-shell-search"
      />
      <p v-if="visibleTabs.length === 0" class="nx-settings-shell-search-empty" role="status">
        {{ searchEmptyText }}
      </p>
    </div>

    <nav
      ref="tab-list"
      class="nx-settings-shell-tabs"
      data-nx-scope="sidebar"
      role="tablist"
      :aria-label="title"
    >
      <div v-for="group in groupedTabs" :key="group.id" class="nx-settings-shell-tab-group">
        <span v-if="group.label" class="nx-settings-shell-tab-group-label">
          {{ group.label }}
        </span>
        <button
          v-for="tab in group.tabs"
          :id="`${idPrefix}-tab-${tab.id}`"
          :key="tab.id"
          :aria-controls="`${idPrefix}-panel-${tab.id}`"
          :aria-selected="activeTabDefinition.id === tab.id"
          :tabindex="focusTabId === tab.id ? 0 : -1"
          class="nx-settings-shell-tab"
          :class="{ active: activeTabDefinition.id === tab.id }"
          role="tab"
          :title="tab.description"
          type="button"
          @click="activeTab = tab.id"
          @keydown="onTabKeydown($event, tab.id)"
        >
          <span v-if="$slots['tab-icon']" aria-hidden="true" class="nx-settings-shell-tab-icon">
            <slot name="tab-icon" :tab="tab" />
          </span>
          <span class="nx-settings-shell-tab-text">
            <span class="nx-settings-shell-tab-label">{{ tab.label }}</span>
            <span
              v-if="tab.description"
              aria-hidden="true"
              class="nx-settings-shell-tab-description"
            >
              {{ tab.description }}
            </span>
          </span>
        </button>
      </div>
    </nav>

    <section
      :id="`${idPrefix}-panel-${activeTabDefinition.id}`"
      :aria-labelledby="`${idPrefix}-tab-${activeTabDefinition.id}`"
      class="nx-settings-shell-content"
      role="tabpanel"
    >
      <div class="nx-settings-shell-content-head">
        <span v-if="$slots['tab-icon']" aria-hidden="true" class="nx-settings-shell-content-icon">
          <slot name="tab-icon" :tab="activeTabDefinition" />
        </span>
        <div>
          <h3>{{ activeTabDefinition.label }}</h3>
          <p v-if="activeTabDefinition.description">{{ activeTabDefinition.description }}</p>
        </div>
      </div>
      <slot :tab="activeTabDefinition" />
    </section>
  </section>
</template>

<style scoped>
.nx-settings-shell {
  display: grid;
  grid-template-columns: calc(var(--nxt-size-unit) * 14.375) minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  flex: 1;
  min-width: 0;
  min-height: 0;
  background: var(--nxt-surface-bg);
}

.nx-settings-shell,
.nx-settings-shell * {
  box-sizing: border-box;
}

.nx-settings-shell-header {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nxt-space-md);
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  background: var(--nxt-surface-bg);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-settings-shell-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  margin: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.nx-settings-shell-titles h2 {
  margin: 0;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-lg);
}

.nx-settings-shell-header-actions {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
}

.nx-settings-shell.is-searchable {
  grid-template-rows: auto auto minmax(0, 1fr);
}

.nx-settings-shell.is-searchable .nx-settings-shell-content {
  grid-column: 2;
  grid-row: 2 / span 2;
}

.nx-settings-shell-search {
  grid-column: 1;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-sm) var(--nxt-space-xs) 0;
  border-right: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-settings-shell-search-empty {
  margin: 0;
  padding: var(--nxt-space-xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}

.nx-settings-shell-tabs {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-sm) var(--nxt-space-xs);
  overflow-y: auto;
  background: var(--nxd-surface-bg);
  border-right: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-settings-shell-tab-group {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}

.nx-settings-shell-tab-group-label {
  padding: 0 var(--nxt-space-xs) var(--nxt-space-2xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.nx-settings-shell-tab {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-height: var(--nxt-control-size-xl);
  padding: var(--nxt-space-xs);
  color: var(--nxt-fg);
  background: transparent;
  border: var(--nxt-hairline) solid transparent;
  border-radius: var(--nxt-radius-md);
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition:
    background 0.14s ease,
    border-color 0.14s ease,
    color 0.14s ease;
}

.nx-settings-shell-tab:hover {
  border-color: var(--nxt-border);
}

.nx-settings-shell-tab:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-brand-bg);
  outline-offset: calc(var(--nxt-size-unit) * 0.0625);
}

.nx-settings-shell-tab.active {
  color: var(--nxt-fg);
  background: var(--nxd-surface-bg);
  border-color: var(--nxt-border-strong);
  box-shadow:
    inset calc(var(--nxt-hairline) * 3) 0 0 var(--nxt-brand-bg),
    var(--nxt-shadow-sm);
}

.nx-settings-shell-tab-icon {
  display: inline-grid;
  flex: 0 0 auto;
  place-items: center;
  width: var(--nxt-control-size-md);
  height: var(--nxt-control-size-md);
  color: var(--nxt-fg-muted);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
}

.nx-settings-shell-tab.active .nx-settings-shell-tab-icon {
  color: var(--nxt-brand-fg);
  background: var(--nxt-brand-bg);
  border-color: var(--nxt-brand-bg);
}

.nx-settings-shell-tab-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.nx-settings-shell-tab-label {
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
}

.nx-settings-shell-tab-description {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nx-settings-shell-content {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-md);
  padding: var(--nxt-space-md);
  overflow-y: auto;
  background: var(--nxd-surface-bg);
}

.nx-settings-shell-content-head {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-sm);
  padding-bottom: var(--nxt-space-sm);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-settings-shell-content-icon {
  display: grid;
  place-items: center;
  width: var(--nxt-tile-size-sm);
  height: var(--nxt-tile-size-sm);
  color: var(--nxt-brand-fg);
  background: var(--nxt-brand-bg);
  border-radius: var(--nxt-radius-md);
}

.nx-settings-shell-content-head h3 {
  margin: 0;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-md);
}

.nx-settings-shell-content-head p {
  margin: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}

@media (max-width: 53.75em) {
  .nx-settings-shell {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto minmax(0, 1fr);
  }

  .nx-settings-shell-header {
    flex-wrap: wrap;
    align-items: flex-start;
  }

  .nx-settings-shell-tabs {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--nxt-space-xs);
    max-height: var(--nxt-area-size-xl);
    border-right: 0;
    border-bottom: var(--nxt-hairline) solid var(--nxt-border);
  }

  .nx-settings-shell.is-searchable {
    grid-template-rows: auto auto auto minmax(0, 1fr);
  }

  .nx-settings-shell.is-searchable .nx-settings-shell-content {
    grid-column: 1;
    grid-row: auto;
  }

  .nx-settings-shell-search {
    padding-bottom: var(--nxt-space-xs);
    border-right: 0;
  }

  .nx-settings-shell-tab-group {
    display: contents;
  }

  .nx-settings-shell-tab-group-label {
    grid-column: 1 / -1;
    padding: var(--nxt-space-2xs) var(--nxt-space-2xs) 0;
  }
}

@media (max-width: 35em) {
  .nx-settings-shell-content {
    padding: var(--nxt-space-sm);
  }

  .nx-settings-shell-tab-description,
  .nx-settings-shell-content-head p,
  .nx-settings-shell-eyebrow {
    display: none;
  }
}
</style>
